import React from 'react';
import { Palette, PaintBucket, Type } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';

const SHAPE_COLORS = [
  '#FFFFFF', '#F3F4F6', '#E5E7EB', '#D1D5DB',
  '#9CA3AF', '#6B7280', '#374151', '#1F2937',
  '#000000', '#EF4444', '#F97316', '#F59E0B',
  '#EAB308', '#84CC16', '#22C55E', '#10B981',
  '#06B6D4', '#3B82F6', '#6366F1', '#EC4899'
];

const FILL_COLORS = [
  'transparent',
  '#FFFFFF', '#F3F4F6', '#E5E7EB', '#D1D5DB',
  '#FEE2E2', '#FED7AA', '#FEF08A', '#D9F99D',
  '#BBF7D0', '#A7F3D0', '#99F6E4', '#BAE6FD',
  '#C7D2FE', '#DDD6FE', '#F5D0FE', '#FBCFE8'
];

const TEXT_COLORS = [
  '#000000', '#1F2937', '#374151', '#6B7280',
  '#9CA3AF', '#FFFFFF', '#EF4444', '#F97316',
  '#F59E0B', '#EAB308', '#84CC16', '#22C55E',
  '#10B981', '#06B6D4', '#3B82F6', '#6366F1',
  '#8B5CF6', '#A855F7', '#EC4899', '#F43F5E'
];

export default function ShapePropertiesPanel({ 
  element, 
  onUpdate,
  position 
}) {
  const shapeTypes = ['shape_rect', 'shape_circle'];
  if (!element || !shapeTypes.includes(element.type)) return null;
  
  const props = element.properties || {};
  const currentStroke = props.stroke || '#000000';
  const currentFill = props.fill || 'transparent';
  const currentStrokeWidth = props.strokeWidth || 2;
  const currentTextColor = props.textColor || '#000000';
  const currentFontSize = props.fontSize || 16;
  const isRect = element.type === 'shape_rect';

  const handleStrokeChange = (color) => {
    onUpdate(element.id, {
      properties: { ...element.properties, stroke: color }
    });
  };

  const handleFillChange = (color) => {
    onUpdate(element.id, {
      properties: { ...element.properties, fill: color }
    });
  };

  const handleStrokeWidthChange = (width) => {
    onUpdate(element.id, {
      properties: { ...element.properties, strokeWidth: width }
    });
  };

  const handleTextColorChange = (color) => {
    onUpdate(element.id, {
      properties: { ...element.properties, textColor: color }
    });
  };

  const handleFontSizeChange = (size) => {
    onUpdate(element.id, {
      properties: { ...element.properties, fontSize: size }
    });
  };

  return (
    <div 
      className="absolute z-50 flex items-center gap-1 bg-background rounded-lg border shadow-lg p-1"
      style={{ 
        left: position?.x || 100, 
        top: Math.max(10, (position?.y || 100) - 50)
      }}
    >
      {/* Fill Color Picker */}
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 px-2 gap-2"
            data-testid="shape-fill-picker"
          >
            <div 
              className="w-4 h-4 rounded border border-gray-300"
              style={{ 
                backgroundColor: currentFill === 'transparent' ? 'white' : currentFill,
                backgroundImage: currentFill === 'transparent' 
                  ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
                  : 'none',
                backgroundSize: '8px 8px',
                backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px'
              }}
            />
            <PaintBucket className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" side="bottom">
          <p className="text-xs font-medium mb-2">Fill Color</p>
          <div className="grid grid-cols-5 gap-1">
            {FILL_COLORS.map((color, index) => (
              <button
                key={color + index}
                className={`w-6 h-6 rounded border-2 transition-all ${
                  currentFill === color 
                    ? 'border-primary scale-110' 
                    : 'border-transparent hover:border-gray-400'
                }`}
                style={{ 
                  backgroundColor: color === 'transparent' ? 'white' : color,
                  backgroundImage: color === 'transparent' 
                    ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
                    : 'none',
                  backgroundSize: '6px 6px',
                  backgroundPosition: '0 0, 0 3px, 3px -3px, -3px 0px'
                }}
                onClick={() => handleFillChange(color)}
                title={color === 'transparent' ? 'No fill' : color}
                data-testid={`fill-color-${color.replace('#', '')}`}
              />
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Stroke Color Picker */}
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 px-2 gap-2"
            data-testid="shape-stroke-picker"
          >
            <div 
              className="w-4 h-4 rounded-full border-2"
              style={{ borderColor: currentStroke, backgroundColor: 'transparent' }}
            />
            <Palette className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" side="bottom">
          <p className="text-xs font-medium mb-2">Border Color</p>
          <div className="grid grid-cols-5 gap-1">
            {SHAPE_COLORS.map(color => (
              <button
                key={color}
                className={`w-6 h-6 rounded-full border-2 transition-all ${
                  currentStroke === color 
                    ? 'border-primary scale-110' 
                    : 'border-transparent hover:border-gray-400'
                }`}
                style={{ backgroundColor: color }}
                onClick={() => handleStrokeChange(color)}
                data-testid={`stroke-color-${color.replace('#', '')}`}
              />
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Stroke Width */}
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 px-2 gap-1"
            data-testid="shape-stroke-width"
          >
            <div className="flex items-center gap-1">
              <div 
                className="w-6 rounded-full"
                style={{ 
                  height: Math.min(currentStrokeWidth, 6), 
                  backgroundColor: currentStroke,
                  minHeight: 2
                }}
              />
              <span className="text-xs">{currentStrokeWidth}px</span>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-3" side="bottom">
          <p className="text-xs font-medium mb-2">Border Width: {currentStrokeWidth}px</p>
          <Slider
            value={[currentStrokeWidth]}
            onValueChange={([val]) => handleStrokeWidthChange(val)}
            min={1}
            max={10}
            step={1}
          />
        </PopoverContent>
      </Popover>

      {/* Text Color Picker - Only for rectangles */}
      {isRect && (
        <Popover>
          <PopoverTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 px-2 gap-2"
              data-testid="shape-text-color-picker"
            >
              <Type className="h-4 w-4" style={{ color: currentTextColor }} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" side="bottom">
            <p className="text-xs font-medium mb-2">Text Color</p>
            <div className="grid grid-cols-5 gap-1">
              {TEXT_COLORS.map(color => (
                <button
                  key={color}
                  className={`w-6 h-6 rounded-full border-2 transition-all ${
                    currentTextColor === color 
                      ? 'border-primary scale-110' 
                      : 'border-transparent hover:border-gray-400'
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => handleTextColorChange(color)}
                  data-testid={`text-color-${color.replace('#', '')}`}
                />
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Font Size - Only for rectangles */}
      {isRect && (
        <Popover>
          <PopoverTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 px-2 gap-1"
              data-testid="shape-font-size"
            >
              <span className="text-xs font-medium">{currentFontSize}px</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3" side="bottom">
            <p className="text-xs font-medium mb-2">Text Size: {currentFontSize}px</p>
            <Slider
              value={[currentFontSize]}
              onValueChange={([val]) => handleFontSizeChange(val)}
              min={10}
              max={48}
              step={2}
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
